import { Flame, Sparkles } from "lucide-react"
import { useTranslation } from "react-i18next"

import { cn } from "@/lib/utils"
import type { WelcomeBackStreak } from "@/types/welcome-back"

/**
 * OKS-560 — §21 streak-jeopardy row (UX Spec §2.3 / §3 edge).
 *
 * Renders ONE of two lines depending on the shipped `Streaks::StatusResolver`
 * state the server emitted:
 *   - `at_risk` → "🔥 Your {N}-day streak resets if you miss this drop." Amber
 *                 flame accent + text label (never colour-only — the "resets if
 *                 you miss" meaning is in the text).
 *   - `broken`  → clean-start line "Fresh start — begin a new streak today." No
 *                 🔥-loss copy, no guilt (the edge state, PRD §5).
 *   - anything else (active / frozen) → renders nothing (the streak is safe;
 *                 no jeopardy, no clean-start).
 *
 * The softer, warmer drop-relative copy is kept over an hours countdown (UX
 * Q3); `at_risk_hours` is available on the prop for a future sharpening.
 *
 * # Locked testids
 *   - `welcome-back-jeopardy`    — at-risk row
 *   - `welcome-back-clean-start` — reset/clean-start row
 */
interface StreakJeopardyRowProps {
  streak: WelcomeBackStreak
  className?: string
}

export function StreakJeopardyRow({
  streak,
  className,
}: StreakJeopardyRowProps) {
  const { t } = useTranslation()

  if (streak.state === "at_risk") {
    return (
      <div
        data-testid="welcome-back-jeopardy"
        className={cn(
          "flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2.5 text-sm font-medium text-amber-700 dark:bg-amber-950/30 dark:text-amber-400",
          className,
        )}
      >
        <Flame className="size-4 shrink-0" aria-hidden="true" />
        <span>
          {t("creator_welcome_back.jeopardy.at_risk", {
            count: streak.current_streak_days,
          })}
        </span>
      </div>
    )
  }

  if (streak.state === "broken") {
    return (
      <div
        data-testid="welcome-back-clean-start"
        className={cn(
          "text-muted-foreground flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium",
          className,
        )}
      >
        <Sparkles className="size-4 shrink-0" aria-hidden="true" />
        <span>{t("creator_welcome_back.jeopardy.clean_start")}</span>
      </div>
    )
  }

  return null
}
